import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Category chrome and featured bands rise once per page, not on every drill. */
const riseSettledSurfaces = new Set<string>();
const riseFirstToken = new Map<string, string>();

export function useOneTimeRise(
  surface: string,
  active: boolean,
  token: string,
  /** False while the chips are still a skeleton. The rise only locks after they paint. */
  ready = true,
) {
  const [settled, setSettled] = useState(() => riseSettledSurfaces.has(surface));

  useLayoutEffect(() => {
    if (!active || riseSettledSurfaces.has(surface)) {
      if (riseSettledSurfaces.has(surface)) setSettled(true);
      return;
    }
    // Facets can land after mount. Don't treat that wait, or a token that
    // hydrates during it, as a drill the user already saw.
    if (!ready) return;
    const seen = riseFirstToken.get(surface);
    if (seen === undefined) {
      riseFirstToken.set(surface, token);
      return;
    }
    if (seen !== token) {
      riseSettledSurfaces.add(surface);
      setSettled(true);
    }
  }, [active, ready, surface, token]);

  useEffect(() => {
    if (!active || !ready || settled) return;
    const t = window.setTimeout(() => {
      riseSettledSurfaces.add(surface);
      setSettled(true);
    }, 800);
    return () => window.clearTimeout(t);
  }, [active, ready, settled, surface]);

  return settled;
}

const SKELETON_PILL_WIDTHS = [68, 56, 82, 64, 74, 50, 78, 60, 70];

/**
 * Placeholder row shown while browse/content filters are still in-flight.
 * Same height as `ShopBrowseNav` so the grid below never jumps.
 */
export function ShopBrowseNavSkeleton() {
  return (
    <div
      className="mte-tpl-shop-browse"
      aria-hidden="true"
      data-website-tour="shop-breadcrumbs"
    >
      <div className="mte-tpl-shop-browse-row" data-no-scrollbar>
        {SKELETON_PILL_WIDTHS.map((w, i) => (
          <span
            key={i}
            className="mte-tpl-shop-browse-skeleton-pill"
            style={{
              width: w,
              animationDelay: `${i * 110}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export type ShopBrowseNavOption = {
  label: string;
  onClick: () => void;
};

export type ShopBrowseNavCrumb = {
  label: string;
  onClear: () => void;
};

type ShopBrowseNavProps = {
  crumbs?: ShopBrowseNavCrumb[];
  options?: ShopBrowseNavOption[];
  isMobile?: boolean;
  /** After the first rise, later drill-ins and closes stay still. */
  riseSettled?: boolean;
};

function NavCrumb({ label, onClear, index = 0 }: ShopBrowseNavCrumb & { index?: number }) {
  return (
    <button
      type="button"
      className="mte-tpl-shop-browse-crumb"
      style={{ ["--mte-tile-stagger" as string]: String(index % 8) }}
      onClick={onClear}
    >
      {label}
      <span className="mte-tpl-shop-browse-crumb-x" aria-hidden>
        ×
      </span>
    </button>
  );
}

function NavOption({ label, onClick, index = 0 }: ShopBrowseNavOption & { index?: number }) {
  return (
    <button
      type="button"
      className="mte-tpl-shop-browse-option"
      style={{ ["--mte-tile-stagger" as string]: String(index % 8) }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/** Reset options so a new layer always starts at the beginning of the list. */
function scrollBrowseOptionsToStart(el: HTMLElement | null) {
  if (!el) return;
  el.scrollTo({ left: 0, behavior: "auto" });
}

/**
 * Single-row shop filter: selected crumbs stay pinned on the left; the next
 * level's options scroll horizontally beside them.
 */
export function ShopBrowseNav({
  crumbs = [],
  options = [],
  riseSettled = false,
}: ShopBrowseNavProps) {
  const optionsRef = useRef<HTMLDivElement>(null);
  const crumbKey = crumbs.map((c) => c.label).join(">");
  const optionKey = options.map((o) => o.label).join(">");

  useLayoutEffect(() => {
    scrollBrowseOptionsToStart(optionsRef.current);
  }, [crumbKey, optionKey]);

  if (!crumbs.length && !options.length) return null;

  return (
    <div
      className={`mte-tpl-shop-browse${riseSettled ? " mte-tpl-rise-settled" : ""}`}
      role="navigation"
      aria-label="Browse filters"
      data-website-region="shop-breadcrumbs"
      data-website-tour="shop-breadcrumbs"
    >
      <div className="mte-tpl-shop-browse-row">
        {crumbs.length > 0 ? (
          <div className="mte-tpl-shop-browse-crumbs">
            {crumbs.map((c, i) => (
              <NavCrumb
                key={`crumb-${c.label}-${i}`}
                label={c.label}
                onClear={c.onClear}
                index={i}
              />
            ))}
          </div>
        ) : null}
        {options.length > 0 ? (
          <div
            ref={optionsRef}
            className="mte-tpl-shop-browse-options"
            data-no-scrollbar
          >
            {options.map((t, i) => (
              <NavOption
                key={`opt-${t.label}-${i}`}
                label={t.label}
                onClick={t.onClick}
                index={i}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type ShopSearchBarProps = {
  placeholder: string;
  draft: string;
  onDraft: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  disabled?: boolean;
  onFocus?: () => void;
  inputId?: string;
};

/** Filled shop search field — not pill-shaped, so it doesn't read as a filter chip. */
export function ShopSearchBar({
  placeholder,
  draft,
  onDraft,
  onSubmit,
  onClear,
  disabled = false,
  onFocus,
  inputId,
}: ShopSearchBarProps) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="mte-tpl-shop-search">
      <div
        className={`mte-tpl-shop-search-bar${focused ? " is-focused" : ""}`}
      >
        <svg
          className="mte-tpl-shop-search-icon"
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          aria-hidden
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          id={inputId}
          className="mte-tpl-shop-search-input"
          type="search"
          placeholder={placeholder}
          value={draft}
          readOnly={disabled}
          onChange={disabled ? undefined : (e) => onDraft(e.target.value)}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            } else if (e.key === "Escape") {
              onClear();
            }
          }}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => setFocused(false)}
          enterKeyHint="search"
          autoComplete="off"
        />
        {/* Always mounted so typing / clear never changes bar height. */}
        <button
          type="button"
          className={
            draft
              ? "mte-tpl-shop-search-clear"
              : "mte-tpl-shop-search-clear mte-tpl-shop-search-clear--hidden"
          }
          onClick={onClear}
          aria-label="Clear search"
          aria-hidden={!draft}
          tabIndex={draft ? 0 : -1}
          disabled={!draft || disabled}
        >
          ×
        </button>
      </div>
    </div>
  );
}

type ShopBrowsePanelItem = {
  label: string;
  onClick: () => void;
  /** Stable id for inline-edit path + active highlight. */
  slug?: string;
  /** Optional cover for `cards` display mode. */
  image?: string;
};

export type ShopCategoryDisplayMode = "row" | "column" | "cards";

function ShopBrowsePanelLabel({
  item,
  className,
  active,
  index = 0,
}: {
  item: ShopBrowsePanelItem;
  className: string;
  active?: boolean;
  index?: number;
}) {
  const cls = `${className}${active ? " is-active" : ""}`;
  return (
    <button
      type="button"
      className={cls}
      style={{ ["--mte-tile-stagger" as string]: String(index % 8) }}
      onClick={item.onClick}
    >
      {item.label}
    </button>
  );
}

/**
 * Category / platform directory before drill-in — after a pick, `ShopBrowseNav`
 * takes the same slot.
 *
 * `display` modes (category UI improvements):
 *  - row: horizontal scrolling text (compact)
 *  - column: multi-column wrap (default — denser lists)
 *  - cards: square image tiles with name below
 */
export function ShopBrowsePanel({
  items,
  cols,
  device,
  display = "column",
  activeSlug,
  riseSettled = false,
}: {
  items: ShopBrowsePanelItem[];
  /** Optional preferred column count; still capped by device max. */
  cols?: number;
  /** Caps category columns: 2 on mobile, 4 on desktop/tablet. */
  device?: "desktop" | "tablet" | "mobile";
  display?: ShopCategoryDisplayMode;
  /** Currently filtered primary (or platform) slug. */
  activeSlug?: string | null;
  riseSettled?: boolean;
}) {
  if (!items.length) return null;
  const isMobile = device === "mobile";
  const maxCols = isMobile ? 2 : 4;
  const columnCount = Math.max(1, Math.min(maxCols, cols ?? maxCols));
  const active = String(activeSlug || "")
    .trim()
    .toLowerCase();

  const isActive = (item: ShopBrowsePanelItem) => {
    const slug = String(item.slug || "")
      .trim()
      .toLowerCase();
    return Boolean(active) && slug === active;
  };

  if (display === "row") {
    return (
      <div
        className={`mte-tpl-shop-browse-panel mte-tpl-shop-browse-panel--row${riseSettled ? " mte-tpl-rise-settled" : ""}`}
        data-website-tour="shop-breadcrumbs"
      >
        <div className="mte-tpl-shop-browse-panel-row" data-no-scrollbar>
          {items.map((item, index) => (
            <ShopBrowsePanelLabel
              key={item.slug || item.label}
              item={item}
              className="mte-tpl-shop-browse-panel-row-link"
              active={isActive(item)}
              index={index}
            />
          ))}
        </div>
      </div>
    );
  }

  if (display === "cards") {
    const shown = isMobile ? items.slice(0, 4) : items;
    return (
      <div
        className={`mte-tpl-shop-browse-panel mte-tpl-shop-browse-panel--cards${riseSettled ? " mte-tpl-rise-settled" : ""}`}
        data-website-tour="shop-breadcrumbs"
      >
        <div
          className={`mte-tpl-shop-browse-panel-cards${
            isMobile ? " mte-tpl-shop-browse-panel-cards--wrap" : ""
          }`}
        >
          {shown.map((item, index) => (
            <button
              key={item.slug || item.label}
              type="button"
              className={`mte-tpl-shop-browse-panel-card${
                isActive(item) ? " is-active" : ""
              }`}
              style={{ ["--mte-tile-stagger" as string]: String(index % 8) }}
              onClick={item.onClick}
              style={
                isMobile
                  ? { flex: "0 0 calc(50% - 6px)" }
                  : undefined
              }
            >
              <div className="mte-tpl-shop-browse-panel-card-img">
                {item.image ? (
                  <img src={item.image} alt="" loading="lazy" decoding="async" />
                ) : (
                  <span className="mte-tpl-shop-browse-panel-card-ph" aria-hidden />
                )}
              </div>
              <span className="mte-tpl-shop-browse-panel-card-name">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // column (default)
  // One item per column stretches labels across the full width — looks uneven.
  const inline = items.length <= columnCount;
  const rows = Math.ceil(items.length / columnCount);
  const rowsNarrow = Math.ceil(items.length / Math.min(2, columnCount));

  return (
    <div
      className={`mte-tpl-shop-browse-panel${riseSettled ? " mte-tpl-rise-settled" : ""}`}
      data-website-tour="shop-breadcrumbs"
    >
      <div
        className={
          inline
            ? "mte-tpl-shop-browse-panel-cols mte-tpl-shop-browse-panel-cols--inline"
            : "mte-tpl-shop-browse-panel-cols"
        }
        style={
          inline
            ? undefined
            : ({
                ["--browse-max-cols" as string]: columnCount,
                ["--browse-rows" as string]: rows,
                ["--browse-rows-narrow" as string]: rowsNarrow,
              } as React.CSSProperties)
        }
      >
        {items.map((item, index) => (
          <div
            key={item.slug || item.label}
            className="mte-tpl-shop-browse-panel-item"
          >
            <ShopBrowsePanelLabel
              item={item}
              className="mte-tpl-shop-browse-panel-link"
              active={isActive(item)}
              index={index}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mobile stand-in for the category / platform panel. */
export function ShopFilterToggle({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mte-tpl-shop-filter-bar">
      <button
        type="button"
        className={`mte-tpl-shop-filter-toggle${open ? " is-on" : ""}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        {label}
        <span
          className="mte-tpl-shop-filter-caret"
          style={{ transform: open ? "rotate(180deg)" : undefined }}
          aria-hidden
        >
          ▾
        </span>
      </button>
    </div>
  );
}

export type ShopTextFilter = {
  value: string;
  label: string;
  /** When true, the chip stays visible but cannot be selected (empty for this query). */
  disabled?: boolean;
};

/** Text filters under the browse row — shoppable on Content, scopes on Search. */
export function ShopTextFilterRow({
  items,
  value,
  onChange,
  ariaLabel,
  riseSettled = false,
}: {
  items: ShopTextFilter[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  riseSettled?: boolean;
}) {
  if (!items.length) return null;
  return (
    <div
      className={`mte-tpl-shop-text-filters${riseSettled ? " mte-tpl-rise-settled" : ""}`}
      role="group"
      aria-label={ariaLabel}
    >
      {items.map((item, index) => {
        const disabled = Boolean(item.disabled);
        return (
          <button
            key={item.value}
            type="button"
            className={`mte-tpl-shop-text-filter${value === item.value ? " is-on" : ""}${
              disabled ? " is-disabled" : ""
            }`}
            style={{ ["--mte-tile-stagger" as string]: String(index % 8) }}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            onClick={() => {
              if (disabled) return;
              onChange(item.value);
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/** Parse Controls → Search Bar suggestions (`user.search_bar_tags`, comma-separated). */
export function parseSearchSuggestionTags(raw: unknown): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of String(raw ?? "").split(",")) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export function ShopSearchSuggestions({
  terms,
  onSearch,
}: {
  terms: string[];
  onSearch: (term: string) => void;
}) {
  if (!terms.length) return null;
  return (
    <div className="mte-tpl-shop-suggest">
      <span className="mte-tpl-shop-suggest-label">Try searching</span>
      {terms.map((term) => (
        <button
          key={term}
          type="button"
          className="mte-tpl-shop-dir-chip"
          onClick={() => onSearch(term)}
        >
          {term}
        </button>
      ))}
    </div>
  );
}

export function ShopCatalogEmpty({
  title,
  message,
}: {
  title?: string;
  message: string;
}) {
  return (
    <div className="mte-tpl-shop-catalog-empty" role="status">
      <div className="mte-tpl-shop-catalog-empty-icon" aria-hidden>
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 7h12l-1 12H7L6 7Z" />
          <path d="M9 7V5a3 3 0 0 1 6 0v2" />
        </svg>
      </div>
      {title ? (
        <div className="mte-tpl-shop-catalog-empty-title">{title}</div>
      ) : null}
      <div className="mte-tpl-shop-catalog-empty-msg">{message}</div>
    </div>
  );
}
